import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  UserPlus, 
  Shield, 
  Trash2, 
  Copy,
  CheckCircle,
  AlertTriangle,
  Calendar,
  X,
  Save,
  UserCheck,
  UserX,
  Settings
} from 'lucide-react';
import { useAuth } from '../contexts/JWTAuthContext';
import api from '../utils/api';
import './TeamManagement.css';

const TeamManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'viewer'
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      
      if (response.data.success) {
        setUsers(response.data.users || []);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.post('/api/users', newUser);

      if (response.data.success) {
        setGeneratedPassword(response.data.password);
        setShowPassword(true);
        setNewUser({ email: '', name: '', role: 'viewer' });
        setShowAddForm(false);
        fetchUsers(); // Refresh the list
      } else {
        alert(`Failed to create user: ${response.data.message || response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create user';
      alert(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await api.delete(`/api/users/${userId}`);

      if (response.data.success) {
        fetchUsers(); // Refresh the list
      } else {
        alert(`Failed to delete user: ${response.data.message || response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete user';
      alert(errorMessage);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const response = await api.patch(`/api/users/${userId}`, { is_active: !currentStatus });

      if (response.data.success) {
        fetchUsers(); // Refresh the list
      } else {
        alert(`Failed to update user: ${response.data.message || response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update user';
      alert(errorMessage);
    }
  };

  const handleEditRole = (user) => {
    setEditingUser(user.id);
    setEditingRole(user.role);
  };

  const handleSaveRole = async (userId) => {
    try {
      const response = await api.patch(`/api/users/${userId}`, { role: editingRole });

      if (response.data.success) {
        setEditingUser(null);
        setEditingRole('');
        fetchUsers(); // Refresh the list
      } else {
        alert(`Failed to update user role: ${response.data.message || response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update user role';
      alert(errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditingRole('');
  };

  // Check if user is admin after all hooks
  if (!user || user.role !== 'admin') {
    return (
      <div className="team-management">
        <motion.div 
          className="access-denied glass-effect"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AlertTriangle size={48} className="access-icon" />
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="team-management">
        <motion.div 
          className="loading glass-effect"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="loading-spinner"></div>
          <p>Loading team members...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="team-management">
      <div className="team-header">
        <div className="header-content">
          <h1 className="team-title">Team Management</h1>
          <p className="team-subtitle">Manage your team members and their permissions</p>
        </div>
        <motion.button 
          className="add-user-btn glass-effect hover-glow"
          onClick={() => setShowAddForm(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <UserPlus size={20} />
          Add User
        </motion.button>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div 
            className="add-user-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAddForm(false)}
          >
            <motion.div 
              className="modal-content glass-effect"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Add New User</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowAddForm(false)}
                >
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleAddUser}>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div className="form-actions">
                  <motion.button 
                    type="submit" 
                    className="create-btn glass-effect hover-glow"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Save size={16} />
                    Create User
                  </motion.button>
                  <motion.button 
                    type="button" 
                    className="cancel-btn glass-effect"
                    onClick={() => setShowAddForm(false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPassword && (
          <motion.div 
            className="password-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPassword(false)}
          >
            <motion.div 
              className="modal-content glass-effect"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="success-header">
                  <CheckCircle size={32} className="success-icon" />
                  <h3>User Created Successfully!</h3>
                </div>
                <button 
                  className="close-btn"
                  onClick={() => setShowPassword(false)}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="password-info">
                <p className="password-label">Generated Password:</p>
                <div className="password-display">
                  <code>{generatedPassword}</code>
                  <motion.button 
                    className="copy-btn glass-effect hover-glow"
                    onClick={() => navigator.clipboard.writeText(generatedPassword)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Copy size={16} />
                    Copy
                  </motion.button>
                </div>
                <div className="password-warning">
                  <AlertTriangle size={16} />
                  <p>Please save this password securely. It won't be shown again.</p>
                </div>
              </div>
              
              <motion.button 
                className="close-btn glass-effect hover-glow"
                onClick={() => setShowPassword(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Close
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="users-container glass-effect">
        <div className="users-header">
          <h3>Team Members</h3>
          <span className="user-count">{users.length} member{users.length !== 1 ? 's' : ''}</span>
        </div>
        
        {users.length === 0 ? (
          <motion.div 
            className="empty-state glass-effect"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Users size={48} className="empty-icon" />
            <h3>No team members yet</h3>
            <p>Add your first team member to get started</p>
          </motion.div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <motion.tr 
                    key={user.id}
                    className="user-row"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <td className="name-cell">
                      <div className="user-name">{user.name}</div>
                    </td>
                    
                    <td className="email-cell">
                      <div className="user-email">{user.email}</div>
                    </td>
                    
                    <td className="role-cell">
                      {editingUser === user.id ? (
                        <div className="role-edit">
                          <select
                            value={editingRole}
                            onChange={(e) => setEditingRole(e.target.value)}
                            className="role-select"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      ) : (
                        <span className={`role-badge ${user.role}`}>
                          <Shield size={10} />
                          {user.role === 'admin' ? 'Admin' : 'Viewer'}
                        </span>
                      )}
                    </td>
                    
                    <td className="status-cell">
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    
                    <td className="date-cell">
                      <div className="date-info">
                        <Calendar size={14} />
                        <span>{new Date(user.created_at).toLocaleDateString()}</span>
                      </div>
                    </td>
                    
                    <td className="actions-cell">
                      <div className="user-actions">
                        {editingUser === user.id ? (
                          <>
                            <motion.button
                              className="save-btn"
                              onClick={() => handleSaveRole(user.id)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              title="Save changes"
                            >
                              <CheckCircle size={16} />
                            </motion.button>
                            <motion.button
                              className="cancel-btn"
                              onClick={handleCancelEdit}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              title="Cancel editing"
                            >
                              <X size={16} />
                            </motion.button>
                          </>
                        ) : (
                          <>
                            <motion.button
                              className="edit-role-btn"
                              onClick={() => handleEditRole(user)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              title="Edit role"
                            >
                              <Settings size={16} />
                            </motion.button>
                            <motion.button
                              className={`toggle-btn ${user.is_active ? 'deactivate' : 'activate'}`}
                              onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              title={user.is_active ? 'Deactivate user' : 'Activate user'}
                            >
                              {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                            </motion.button>
                            <motion.button
                              className="delete-btn"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              title="Delete user"
                            >
                              <Trash2 size={16} />
                            </motion.button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
